import React from "react";

import { createSelector } from "@reduxjs/toolkit";
import classnames from "classnames";
import { getFormValues } from "redux-form";

import { getBorApplication } from "../api";
import { SUPPORTED_CARRIER_HASH, FORM_NAME } from "../constants";
import { formFormatDate } from "Common/forms/utils";

import makeBem from "Utils/makeBem";

const bem = makeBem("LetterPreview");

type Props = {
  companyName: string | null | undefined;
  address1: string | null | undefined;
  address2: string;
  groupNumber: string;
  date: string | null | undefined;
  carrier: string;
  signatureUri?: string;
  fullName: string;
  contactTitle: string;
  phoneNumber: string;
  email: string;
};

export const letterPreviewPropsFromBorApplication = createSelector(getBorApplication, (borApplication) => {
  if (!borApplication) {
    return {};
  }
  const { borMedicalInsuranceCompany, borOtherMedicalInsuranceCompany } = borApplication;
  let carrier = borMedicalInsuranceCompany !== "other" ? borMedicalInsuranceCompany : borOtherMedicalInsuranceCompany;
  carrier = carrier && (SUPPORTED_CARRIER_HASH[carrier] || carrier);
  const address1 = borApplication.address1 || "";
  const address2 = `${borApplication.city || ""}, ${borApplication.state}, ${borApplication.zip}`;
  return {
    address1,
    address2,
    carrier,
    companyName: borApplication.name,
    contactTitle: borApplication.contactTitle,
    date: formFormatDate(new Date()),
    email: borApplication.email || "",
    fullName: borApplication.contactName,
    groupNumber: borApplication.borMedicalGroupIDNumber,
    phoneNumber: borApplication.contactPhoneNumber || "",
    signatureUri: borApplication.signatureImageDataUrl,
  };
});

const formSelector = getFormValues(FORM_NAME);

export const letterPreviewPropsFromForm = createSelector(getBorApplication, formSelector, (borApplication = {}, formValues) => {
  const { borMedicalInsuranceCompany, borOtherMedicalInsuranceCompany } = formValues;
  let carrier = borMedicalInsuranceCompany !== "other" ? borMedicalInsuranceCompany : borOtherMedicalInsuranceCompany;
  carrier = carrier && (SUPPORTED_CARRIER_HASH[carrier] || carrier);
  const city = formValues.city || borApplication.city;
  const state = formValues.state || borApplication.state;
  const zipCode = formValues.zip || borApplication.zip;

  return {
    address1: formValues.address1 || borApplication.address1,
    address2: [city, state, zipCode].join(", "),
    carrier,
    companyName: borApplication.name,
    contactTitle: formValues.contactTitle,
    date: formFormatDate(new Date()),
    email: formValues.email || formValues.partnerPrimaryEmail,
    fullName: formValues.contactName,
    groupNumber: formValues.borMedicalGroupIDNumber,
    phoneNumber: formValues.contactPhoneNumber || formValues.partnerPrimaryPhoneNumber,
  };
});

export const LetterContent = ({ address1, address2, carrier, companyName, contactTitle, date, email, fullName, groupNumber, phoneNumber, signatureUri }) => (
  <>
    <div>
      <div className={classnames(bem("Title"), bem("Details"))}>{companyName}</div>
      <div className={bem("Details")}>{phoneNumber}</div>
      <div className={bem("Details")}>{address1}</div>
      <div className={bem("Details")}>{address2}</div>
      <div className={bem("Details")}>Group #{groupNumber}</div>
    </div>
    <div className={bem("Body")}>
      <p>
        {date}
        <br />
        Re: Broker of Record Change - {companyName} - Group #{groupNumber}
        <br />
        To: {carrier}
      </p>
      <p>To Whom It May Concern,</p>
      <p>
        Please change our broker of record to Vivek Shah, tax ID #564916482 (DBA SimplyInsured, Agency name Ten 250 Corp), effective immediately. The
        appointment of Vivek Shah rescinds and supersedes all previous agent appointments and shall remain in force until canceled in writing.
      </p>
      <p>Sincerely</p>
      {signatureUri && <img width={300} height={100} src={signatureUri} />}
      <p>
        {fullName},
        <br />
        {contactTitle || "Group Administrator"}
        <br />
        {email}
        <br />
        {phoneNumber}
      </p>
    </div>
  </>
);

const LetterPreview = (props: Props) => (
  <div className={bem("Card")}>
    <LetterContent {...props} />
  </div>
);

export default LetterPreview;
